import { MOBILE_DESKTOP_EMBED_BASE, MOBILE_STANDALONE_BASE } from '@/utils/mobileWorkbenchBase'
import { isStandalonePwaShell } from '@/utils/device'
import axios from 'axios'

export const APP_BUILD_ID = process.env.VUE_APP_BUILD_ID || 'dev'
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000
const BROWSER_SESSION_ALIVE_KEY = 'erp_browser_session_alive'

let reloginInProgress = false

/** 独立 /m 或桌面内嵌移动工作台 */
export function isMobileWorkbenchRoutePath(path) {
  const p = String(
    path || (typeof window !== 'undefined' ? window.location.pathname : '')
  )
  return (
    p === MOBILE_STANDALONE_BASE ||
    p.startsWith(`${MOBILE_STANDALONE_BASE}/`) ||
    p === MOBILE_DESKTOP_EMBED_BASE ||
    p.startsWith(`${MOBILE_DESKTOP_EMBED_BASE}/`)
  )
}

/**
 * 清理登录态并进入登录页（replace，避免返回键回到失效前的移动页）。
 * @param {string|null} message
 */
export async function forceAuthRelogin(message = null) {
  if (reloginInProgress) return
  reloginInProgress = true
  try {
    try {
      const { useUserStore } = await import('@/store/user')
      useUserStore().clearUser()
    } catch {
      clearAuthStorage()
    }

    const loginPath = '/login'
    let navigated = false
    try {
      const router = (await import('@/router')).default
      if (router.currentRoute.value.path !== loginPath) {
        await router.replace(loginPath)
      }
      navigated = true
    } catch {
      /* router 未就绪时走硬跳转 */
    }
    if (
      !navigated &&
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith(loginPath)
    ) {
      window.location.replace(loginPath)
    }

    if (message) {
      const { ElMessage } = await import('element-plus')
      ElMessage.warning(message)
    }
  } finally {
    setTimeout(() => {
      reloginInProgress = false
    }, 1500)
  }
}

/** 从后台/缓存页恢复时复核移动工作台会话 */
export function setupMobileSessionWatch() {
  if (typeof window === 'undefined') return

  let checking = false
  async function checkMobileSession() {
    if (checking || !isMobileWorkbenchRoutePath()) return
    checking = true
    try {
      if (shouldLogoutForIdle()) {
        await forceAuthRelogin(
          `超过 ${Math.round(IDLE_TIMEOUT_MS / 60000)} 分钟未操作，请重新登录`
        )
        return
      }
      const token = localStorage.getItem('token')
      if (!token) {
        await forceAuthRelogin('请先登录')
        return
      }
      const { useUserStore } = await import('@/store/user')
      const store = useUserStore()
      store.initFromStorage()
      await store.syncUserFromServer({ force: true })
      if (!store.isLoggedIn) {
        await forceAuthRelogin('登录已失效，请重新登录')
      }
    } finally {
      checking = false
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkMobileSession()
  })
  window.addEventListener('pageshow', (event) => {
    // 仅从 bfcache 恢复时复核；勿在每次 pageshow 都 force verify（易误判登出）
    if (event.persisted) {
      checkMobileSession()
    }
  })
}

export function clearAuthStorage() {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('last_active_at')
  try {
    // Login.vue 曾写入默认头；不清理会导致下一账号请求短暂带上旧 Bearer
    delete axios.defaults.headers.common.Authorization
  } catch {
    /* ignore */
  }
}

export function markUserActive() {
  localStorage.setItem('last_active_at', String(Date.now()))
}

export function shouldLogoutForIdle() {
  const token = localStorage.getItem('token')
  if (!token) return false
  const lastActiveAt = Number(localStorage.getItem('last_active_at') || 0)
  if (!lastActiveAt) return false
  return Date.now() - lastActiveAt > IDLE_TIMEOUT_MS
}

export function enforceBuildLoginRefresh() {
  const previousBuildId = localStorage.getItem('app_build_id')
  if (previousBuildId && previousBuildId !== APP_BUILD_ID) {
    clearAuthStorage()
    // 新主包已加载但浏览器仍可能缓存旧异步 chunk；刷新一次以拉齐 dist
    const reloadKey = 'erp_build_id_reload'
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1')
      window.location.reload()
      return
    }
    sessionStorage.removeItem(reloadKey)
  }
  localStorage.setItem('app_build_id', APP_BUILD_ID)
}

/**
 * 由脚本 window.open 打开的同源子窗口：sessionStorage 与父页隔离，但不应视为「新浏览器会话」。
 * 否则子页启动时会误清 localStorage 中的登录态（配料/外发「新标签打开库存」等场景会直接进登录页）。
 */
function isSameOriginAuxiliaryWindow() {
  if (!window.opener || window.opener === window) return false
  try {
    return window.opener.location.origin === window.location.origin
  } catch {
    return false
  }
}

export { isStandalonePwaShell } from '@/utils/device'

/**
 * 约束为“关闭浏览器后需重新登录”：
 * - sessionStorage 会在浏览器进程关闭后清空
 * - 如果发现是新浏览器会话，则清理上次遗留 token/user
 * - 例外：同源 window.open 子页 sessionStorage 恒为空，不得清 token（见 isSameOriginAuxiliaryWindow）
 * - 例外：PWA/主屏幕独立窗口冷启动 sessionStorage 也为空，不得清 token（否则白屏或反复登录）
 */
export function enforceReLoginAfterBrowserClose() {
  if (isStandalonePwaShell()) {
    sessionStorage.setItem(BROWSER_SESSION_ALIVE_KEY, '1')
    return
  }
  const hasAliveMark = sessionStorage.getItem(BROWSER_SESSION_ALIVE_KEY) === '1'
  if (!hasAliveMark && !isSameOriginAuxiliaryWindow()) {
    clearAuthStorage()
  }
  sessionStorage.setItem(BROWSER_SESSION_ALIVE_KEY, '1')
}
